// README!!!
// The sorted-Uint32Array helpers below are deliberately NOT generic over
// `number[] | Uint32Array`: they run over million-element arrays in render-path
// hot loops (SelectionSet operations), and a polymorphic element access would
// deoptimize those loops in V8. Callers with plain arrays should convert once
// (`new Uint32Array(ids)`) rather than widening these signatures.

/**
 * Deduplicate a sorted `Uint32Array`.
 *
 * NOTE: compacts in place and returns the input when nothing was removed;
 * otherwise returns a fresh, exactly-sized copy. Only call on caller-owned
 * arrays (the input must not be shared).
 */
export function dedupeSorted(sorted: Uint32Array): Uint32Array {
    if (sorted.length < 2) {
        return sorted;
    }

    let count = 1;
    for (let i = 1; i < sorted.length; i++) {
        if (sorted[i] !== sorted[i - 1]) {
            sorted[count++] = sorted[i];
        }
    }

    if (count === sorted.length) {
        // No duplicates found — the array is unchanged, return it as is.
        return sorted;
    }

    // Duplicates were compacted away; copy out the first `count` elements so
    // the result is exactly sized (and the oversized input can be collected).
    return sorted.slice(0, count);
}

/**
 * Merge two sorted `Uint32Array`s into a new sorted, deduplicated array.
 */
export function mergeSorted(a: Uint32Array, b: Uint32Array): Uint32Array {
    const merged = new Uint32Array(a.length + b.length);
    let ai = 0;
    let bi = 0;
    let count = 0;
    let last = -1;
    while (ai < a.length || bi < b.length) {
        const next = bi >= b.length || (ai < a.length && a[ai] <= b[bi]) ? a[ai++] : b[bi++];
        if (next !== last) {
            merged[count++] = next;
            last = next;
        }
    }

    if (count === merged.length) {
        // The inputs shared no values — every slot was filled, return as is.
        return merged;
    }

    // Duplicates were skipped, so `merged` has an unused tail; copy out the
    // first `count` elements so the result is exactly sized.
    return merged.slice(0, count);
}

/**
 * Membership test on a sorted `Uint32Array`. Comparator-free counterpart of
 * {@link binarySearch}, kept monomorphic for hot paths.
 */
export function binarySearchIncludes(data: Uint32Array, id: number): boolean {
    let low = 0;
    let high = data.length - 1;
    while (low <= high) {
        const mid = (low + high) >>> 1;
        const value = data[mid];
        if (value === id) {
            return true;
        }

        if (value < id) {
            low = mid + 1;
        } else {
            high = mid - 1;
        }
    }

    return false;
}

/**
 * Build a bitset (1 bit per value in [0..max]) from a sorted, non-empty
 * `Uint32Array`. Costs `max/8` bytes; intended for dense id sets where O(1)
 * membership beats binary search.
 */
export function buildBitset(sorted: Uint32Array): Uint8Array {
    const maxId = sorted[sorted.length - 1];
    const bitset = new Uint8Array((maxId >>> 3) + 1);
    for (const id of sorted) {
        bitset[id >>> 3] |= 1 << (id & 7);
    }

    return bitset;
}

/**
 *
 * @param arr - The array to search.
 * @param target - The target value to search for.
 * @param compareFn - The comparison function to use.
 * @returns
 */
export function binarySearch<T>(arr: readonly T[], target: T, compareFn: (a: T, b: T) => number): number {
    let lo = 0;
    let hi = arr.length - 1;

    while (lo <= hi) {
        const mid = (lo + hi) >>> 1;
        const cmp = compareFn(arr[mid], target);
        if (cmp === 0) {
            return mid;
        }

        if (cmp < 0) {
            lo = mid + 1;
        } else {
            hi = mid - 1;
        }
    }

    return -1;
}
